import './UserMenu.css'

import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useAccount,
  useMsal,
} from '@azure/msal-react'
import { PrimeIcons } from 'primereact/api'
import { Avatar } from 'primereact/avatar'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { OverlayPanel } from 'primereact/overlaypanel'

import { useIsMaintenanceModeEnabled } from '../../../data/tenants'
import { useCurrentTenantConfig } from '../../../utils/tenants'
import LoginButton from '../LoginButton'
import LogoutButton from '../LogoutButton'
import TenantInfo from '../TenantInfo'

export const UserMenu: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { accounts } = useMsal()
  const account = useAccount(accounts[0])
  const currentTenantConfig = useCurrentTenantConfig()
  const panelRef = useRef<OverlayPanel | null>(null)
  const { data: isMaintenanceModeEnabled } = useIsMaintenanceModeEnabled()

  const getInitials = (name: string | undefined): string => {
    if (!name) return 'N/A'
    const lastWhiteSpacePos = name.lastIndexOf(' ')
    const firstLetter = name.substring(0, 1)
    const lastLetter = name.substring(
      lastWhiteSpacePos + 1,
      lastWhiteSpacePos + 2,
    )
    const initials = firstLetter + lastLetter
    return initials.toUpperCase()
  }

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): void => {
    panelRef.current?.toggle(e, undefined)
  }

  return (
    <>
      <Button
        className="p-button-link p-1 border-none"
        onClick={e => handleClick(e)}
      >
        <Avatar
          label={getInitials(account?.name)}
          shape="circle"
          className="header__user-menu__avatar"
        />
      </Button>

      <OverlayPanel ref={panelRef}>
        <AuthenticatedTemplate>
          <div className="flex flex-column">
            <div className="flex">
              <div className="mr-2">
                <Avatar
                  label={getInitials(account?.name)}
                  size="large"
                  shape="circle"
                  className="header__user-menu__avatar"
                />
              </div>
              <div className="mr-2 flex flex-column justify-content-around">
                <div className="font-bold">{account?.name}</div>
                <div className="text-sm text-color-secondary">
                  {account?.username}
                </div>
              </div>
            </div>
            <Divider />
            <div className="flex justify-content-between">
              <div className="mr-2 flex flex-column justify-content-around">
                <TenantInfo
                  tenantId={currentTenantConfig?.tenantId as string}
                />
              </div>
              <div className="mr-2">
                <Button
                  label={t('tenant.profileButton.button')}
                  icon={PrimeIcons.SYNC}
                  onClick={e => {
                    panelRef.current?.toggle(e, undefined)
                    navigate('/set-tenant')
                  }}
                />
              </div>
            </div>
            <Divider />
            <LogoutButton />
          </div>
        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <LoginButton isMaintenanceActive={isMaintenanceModeEnabled!} />
        </UnauthenticatedTemplate>
      </OverlayPanel>
    </>
  )
}
